import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import ListingCard from "../components/ListingCard";

const BEDS_OPTS = [["", "Any beds"], ["0", "Studio"], ["1", "1 bed"], ["2", "2 beds"], ["3", "3 beds"]];

export default function Listings() {
  const [listings, setListings] = useState([]);
  const [hoods, setHoods] = useState([]);
  const [filters, setFilters] = useState({ neighborhood: "", beds: "", min_rent: "", max_rent: "", furnished: "", pet_friendly: "", q: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/neighborhoods").then((r) => setHoods(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ""));
    api.get("/listings", { params }).then((r) => setListings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 md:py-16">
      <SEO title="Browse rental listings in Vancouver — Tendly"
        description="Real, de-duplicated long-term rental listings across Vancouver neighbourhoods. Filter by budget, beds, pets, parking and more." />
      <p className="text-xs uppercase tracking-widest font-semibold text-primary/80">Inventory</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">Browse homes in Vancouver</h1>
      <p className="mt-3 text-muted-foreground max-w-2xl">Every home is submitted or reviewed by our team before it goes live. Want us to do the work? <a href="/chat" className="text-primary font-semibold hover:underline">Open the concierge chat</a>.</p>

      <div className="mt-8 rounded-2xl border border-border bg-card p-5 shadow-sm grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" role="search">
        <input data-testid="filter-q" value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Search area or address"
          aria-label="Search listings" className="col-span-2 rounded-xl border border-input bg-background px-4 py-2 min-h-[44px] text-sm" />
        <select data-testid="filter-neighborhood" value={filters.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} aria-label="Neighbourhood"
          className="rounded-xl border border-input bg-background px-3 py-2 min-h-[44px] text-sm">
          <option value="">All areas</option>
          {hoods.map((h) => <option key={h.slug} value={h.name}>{h.name}</option>)}
        </select>
        <select data-testid="filter-beds" value={filters.beds} onChange={(e) => set("beds", e.target.value)} aria-label="Bedrooms"
          className="rounded-xl border border-input bg-background px-3 py-2 min-h-[44px] text-sm">
          {BEDS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input data-testid="filter-min-rent" type="number" value={filters.min_rent} onChange={(e) => set("min_rent", e.target.value)} placeholder="Min $/mo"
          aria-label="Minimum rent" className="rounded-xl border border-input bg-background px-3 py-2 min-h-[44px] text-sm" />
        <input data-testid="filter-max-rent" type="number" value={filters.max_rent} onChange={(e) => set("max_rent", e.target.value)} placeholder="Max $/mo"
          aria-label="Maximum rent" className="rounded-xl border border-input bg-background px-3 py-2 min-h-[44px] text-sm" />
      </div>
      <div className="mt-3 flex gap-3">
        <select data-testid="filter-furnished" value={filters.furnished} onChange={(e) => set("furnished", e.target.value)} aria-label="Furnished"
          className="rounded-xl border border-input bg-background px-3 py-2 min-h-[44px] text-sm">
          <option value="">Furnished: any</option><option value="furnished">Furnished</option>
          <option value="unfurnished">Unfurnished</option><option value="partially">Partially</option>
        </select>
        <select data-testid="filter-pets" value={filters.pet_friendly} onChange={(e) => set("pet_friendly", e.target.value)} aria-label="Pet policy"
          className="rounded-xl border border-input bg-background px-3 py-2 min-h-[44px] text-sm">
          <option value="">Pets: any</option><option value="yes">Pet friendly</option>
          <option value="cats_only">Cats only</option><option value="case_by_case">Case by case</option>
        </select>
      </div>

      <p data-testid="listings-count" className="mt-6 text-sm text-muted-foreground">{loading ? "Loading…" : `${listings.length} homes`}</p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {listings.map((l) => <ListingCard key={l.listing_id} listing={l} />)}
      </div>
      {!loading && !listings.length && (
        <div className="mt-16 text-center" data-testid="listings-empty">
          <MapPin className="w-8 h-8 mx-auto text-muted-foreground" />
          <p className="mt-3 font-semibold">{Object.values(filters).some((v) => v !== "") ? "No homes match those filters" : "No homes listed here yet."}</p>
          <p className="text-sm text-muted-foreground mt-1">Start a chat and we'll help you find one.</p>
          <a href="/chat" data-testid="listings-empty-chat" className="mt-5 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-2.5 text-sm font-semibold hover:bg-coral-600 transition-colors">Open live chat</a>
        </div>
      )}
    </div>
  );
}
